import { useTheme, useThemeObject } from './ThemeProvider'
import type { Theme } from './theme'

/**
 * Hook to access theme context re-exported from ThemeProvider for convenience
 */
export { useTheme, useThemeMode, useThemeObject, useToggleTheme, useSafeTheme } from './ThemeProvider'

/**
 * Hook to get a specific theme value by path
 * 
 * @example
 * ```tsx
 * const bgColor = useThemeValue('button.primary.bg')
 * const textColor = useThemeValue('text.primary')
 * `
 */
export const useThemeValue = (path: string): string => {
  const theme = useThemeObject()
  
  const keys = path.split('.')
  let value: any = theme
  
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key as keyof typeof value]
    } else {
      console.warn(`Theme path "${path}" not found`)
      return ''
    }
  }
  
  return typeof value === 'string' ? value : ''
}

/**
 * Hook to get button colors
 * 
 * @example
 * ```tsx
 * const { bg, text, bgHover } = useButtonTheme('primary')
 * ```
 */
export const useButtonTheme = (variant: keyof Theme['button']) => {
  const theme = useThemeObject()
  return theme.button[variant]
}

/**
 * Hook to get text colors
 * 
 * @example
 * ```tsx
 * const { primary, secondary, link } = useTextTheme()
 * ```
 */
export const useTextTheme = () => {
  const theme = useThemeObject()
  return theme.text
}

/**
 * Hook to get background colors
 * 
 * @example
 * ```tsx
 * const { primary, secondary, hover } = useBackgroundTheme()
 * ```
 */
export const useBackgroundTheme = () => {
  const theme = useThemeObject()
  return theme.background
}

/**
 * Hook to get status colors
 * 
 * @example
 * ```tsx
 * const { bg, text, icon } = useStatusTheme('success')
 * ```
 */
export const useStatusTheme = (type: keyof Theme['status']) => {
  const theme = useThemeObject()
  return theme.status[type]
}

/**
 * Hook to get input colors
 * 
 * @example
 * ```tsx
 * const { bg, border, text } = useInputTheme()
 * ```
 */
export const useInputTheme = () => {
  const theme = useThemeObject()
  return theme.input
}

/**
 * Hook to get border colors
 * 
 * @example
 * ```tsx
 * const { primary, focus, error } = useBorderTheme()
 * ```
 */
export const useBorderTheme = () => {
  const theme = useThemeObject()
  return theme.border
}

/**
 * Hook to get surface colors (cards, modals)
 * 
 * @example
 * ```tsx
 * const { primary, elevated, overlay } = useSurfaceTheme()
 * ```
 */
export const useSurfaceTheme = () => {
  const theme = useThemeObject()
  return theme.surface
}

/**
 * Hook to get card colors
 * 
 * @example
 * ```tsx
 * const { bg, border, shadow } = useCardTheme()
 * ```
 */
export const useCardTheme = () => {
  const theme = useThemeObject()
  return theme.card
}

/**
 * Hook to get modal colors
 * 
 * @example
 * ```tsx
 * const { bg, overlay, shadow } = useModalTheme()
 * ```
 */
export const useModalTheme = () => {
  const theme = useThemeObject()
  return theme.modal
}

/**
 * Hook to get navigation colors
 * 
 * @example
 * ```tsx
 * const { bg, text, bgHover } = useNavTheme()
 * ```
 */
export const useNavTheme = () => {
  const theme = useThemeObject()
  return theme.nav
}

/**
 * Hook to get table colors
 * 
 * @example
 * ```tsx
 * const { bg, bgHeader, bgRowHover } = useTableTheme()
 * ```
 */
export const useTableTheme = () => {
  const theme = useThemeObject()
  return theme.table
}

/**
 * Hook to get badge colors
 * 
 * @example
 * ```tsx
 * const { bg, text } = useBadgeTheme('primary')
 * ```
 */
export const useBadgeTheme = (variant: keyof Theme['badge']) => {
  const theme = useThemeObject()
  return theme.badge[variant]
}

/**
 * Hook to get chart colors
 * 
 * @example
 * ```tsx
 * const colors = useChartColors()
 * const firstColor = colors[0]
 * ```
 */
export const useChartColors = () => {
  const theme = useThemeObject()
  return theme.chart.colors
}

/**
 * Hook to get work type color
 * 
 * @example
 * ```tsx
 * const color = useWorkTypeColor('damage')
 * ```
 */
export const useWorkTypeColor = (workType: keyof Theme['workType']) => {
  const theme = useThemeObject()
  return theme.workType[workType]
}

/**
 * Hook to get a style object for a button
 * 
 * @example
 * ```tsx
 * const buttonStyle = useButtonStyle('primary')
 * <button style={buttonStyle}>Click me</button>
 * ```
 */
export const useButtonStyle = (
  variant: keyof Theme['button'],
  options?: {
    hover?: boolean
    active?: boolean
    disabled?: boolean
  },
) => {
  const theme = useThemeObject()
  
  if (options?.disabled) {
    const disabledTheme = theme.button.disabled
    return {
      backgroundColor: disabledTheme.bg,
      color: disabledTheme.text,
      borderColor: disabledTheme.border,
      cursor: 'not-allowed',
      opacity: 0.6,
    }
  }
  
  const buttonTheme = theme.button[variant]
  
  // Type guard to check if button has hover/active states
  const hasHoverStates = 'bgHover' in buttonTheme && 'bgActive' in buttonTheme
  
  if (options?.active && hasHoverStates) {
    return {
      backgroundColor: (buttonTheme as any).bgActive,
      color: buttonTheme.text,
      borderColor: buttonTheme.border,
    }
  }
  
  if (options?.hover && hasHoverStates) {
    return {
      backgroundColor: (buttonTheme as any).bgHover,
      color: buttonTheme.text,
      borderColor: buttonTheme.border,
    }
  }
  
  return {
    backgroundColor: buttonTheme.bg,
    color: buttonTheme.text,
    borderColor: buttonTheme.border,
  }
}

/**
 * Hook to get a style object for an input
 * 
 * @example
 * ```tsx
 * const inputStyle = useInputStyle({ focus: true })
 * <input style={inputStyle} />
 * ```
 */
export const useInputStyle = (options?: {
  focus?: boolean
  error?: boolean
  disabled?: boolean
}) => {
  const inputTheme = useInputTheme()
  
  if (options?.disabled) {
    return {
      backgroundColor: inputTheme.bgDisabled,
      color: inputTheme.textDisabled,
      borderColor: inputTheme.border,
      cursor: 'not-allowed',
    }
  }
  
  if (options?.error) {
    return {
      backgroundColor: inputTheme.bg,
      color: inputTheme.text,
      borderColor: inputTheme.borderError,
    }
  }
  
  if (options?.focus) {
    return {
      backgroundColor: inputTheme.bgFocus,
      color: inputTheme.text,
      borderColor: inputTheme.borderFocus,
    }
  }
  
  return {
    backgroundColor: inputTheme.bg,
    color: inputTheme.text,
    borderColor: inputTheme.border,
  }
}

/**
 * Hook to get a style object for a status message
 * 
 * @example
 * ```tsx
 * const statusStyle = useStatusStyle('success')
 * <div style={statusStyle}>Success message</div>
 * ```
 */
export const useStatusStyle = (type: keyof Theme['status']) => {
  const statusTheme = useStatusTheme(type)
  
  return {
    backgroundColor: statusTheme.bg,
    color: statusTheme.text,
    borderColor: statusTheme.border,
  }
}
